import React from "react";
import {
  Briefcase,
  ChevronUp,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Triangle,
} from "lucide-react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import SafetyAnalysis from "../ValueInsights/components/SafetyAnalysis";
import ValuationBreakdown from "./ValuationBreakdown";
import CompetitorsTable from "../ValueInsights/components/CompetitorsTable";
import InsiderActivity from "../ValueInsights/components/InsiderActivity";

const StockDetails = ({
  selectedStock,
  expandedSection,
  toggleSection,
  formatCurrency,
  formatMarketCap,
  historicalPrices,
  financialMetrics,
  competitorData,
  insiderTransactions,
  calculateSafetyRating,
  handleMouseEnter,
  handleMouseLeave,
  setToast,
  handleStockSelect,
}) => {
  return (
    <div className="insights-section">
      <div
        className="section-header clickable"
        onClick={() => toggleSection("stockDetails")}
      >
        <h3>
          <Briefcase size={20} />
          {selectedStock.name} ({selectedStock.symbol}) Analysis
        </h3>
        <div className="section-toggle">
          {expandedSection === "stockDetails" ? (
            <ChevronUp size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
        </div>
      </div>

      {expandedSection === "stockDetails" && (
        <div className="section-content">
          <div className="stock-summary">
            <div className="stock-key-metrics">
              <div className="stock-price-info">
                <div className="current-price">
                  <span>{formatCurrency(selectedStock.currentPrice)}</span>
                  <div
                    className={`trend ${
                      selectedStock.priceChange > 0 ? "up" : "down"
                    }`}
                  >
                    {selectedStock.priceChange > 0 ? (
                      <ArrowUp size={16} />
                    ) : (
                      <ArrowDown size={16} />
                    )}
                    <span>
                      {Math.abs(selectedStock.priceChange).toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="price-intrinsic-comparison">
                  <div className="intrinsic-value">
                    Intrinsic Value:{" "}
                    <span>{formatCurrency(selectedStock.intrinsicValue)}</span>
                  </div>
                  <div className="discount-value">
                    Discount:{" "}
                    <span className="high">
                      {(
                        ((selectedStock.intrinsicValue -
                          selectedStock.currentPrice) /
                          selectedStock.intrinsicValue) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="key-metrics-grid">
                <div className="metric-item">
                  <label>Market Cap</label>
                  <span>{formatMarketCap(selectedStock.marketCap)}</span>
                </div>
                <div className="metric-item">
                  <label>P/E Ratio</label>
                  <span>{selectedStock.peRatio.toFixed(2)}</span>
                </div>
                <div className="metric-item">
                  <label>Dividend Yield</label>
                  <span>{selectedStock.dividendYield.toFixed(2)}%</span>
                </div>
                <div className="metric-item">
                  <label>Beta</label>
                  <span>{selectedStock.beta.toFixed(2)}</span>
                </div>
                <div className="metric-item">
                  <label>ROIC</label>
                  <span>{selectedStock.roic.toFixed(1)}%</span>
                </div>
                <div className="metric-item">
                  <label>Debt-to-Equity</label>
                  <span>{selectedStock.debtToEquity.toFixed(2)}</span>
                </div>
                <div className="metric-item">
                  <label>Growth Rate (5yr)</label>
                  <span>{selectedStock.growthRate.toFixed(1)}%</span>
                </div>
                <div className="metric-item">
                  <label>Operating Margin</label>
                  <span>{selectedStock.operatingMargin.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            <SafetyAnalysis
              selectedStock={selectedStock}
              calculateSafetyRating={calculateSafetyRating}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
            />
          </div>

          <div className="charts-section">
            <div className="chart-container">
              <h4>Price vs. Intrinsic Value History</h4>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={historicalPrices.filter(
                    (item) => item.symbol === selectedStock.symbol
                  )}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), ""]} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#8884d8"
                    name="Market Price"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="intrinsicValue"
                    stroke="#82ca9d"
                    name="Intrinsic Value"
                    strokeDasharray="5 5"
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h4>Financial Metrics Performance</h4>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={financialMetrics.filter(
                    (item) => item.symbol === selectedStock.symbol
                  )}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="revenue"
                    fill="#8884d8"
                    name="Revenue ($B)"
                  />
                  <Line
                    yAxisId="right"
                    dataKey="operatingMargin"
                    stroke="#82ca9d"
                    name="Operating Margin (%)"
                  />
                  <Line
                    yAxisId="right"
                    dataKey="roic"
                    stroke="#ffc658"
                    name="ROIC (%)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <ValuationBreakdown
            selectedStock={selectedStock}
            formatCurrency={formatCurrency}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            setToast={setToast}
          />

          <CompetitorsTable
            selectedStock={selectedStock}
            competitorData={competitorData}
            handleStockSelect={handleStockSelect}
          />

          <InsiderActivity
            selectedStock={selectedStock}
            insiderTransactions={insiderTransactions}
            formatCurrency={formatCurrency}
          />

          <div className="investment-summary">
            <h4>Investment Thesis</h4>
            <div className="thesis-content">
              <p>{selectedStock.investmentThesis}</p>
            </div>

            <div className="strengths-weaknesses">
              <div className="strengths">
                <h5>
                  <TrendingUp size={16} />
                  Strengths
                </h5>
                <ul>
                  {selectedStock.strengths.map((strength, index) => (
                    <li key={index}>{strength}</li>
                  ))}
                </ul>
              </div>

              <div className="weaknesses">
                <h5>
                  <TrendingDown size={16} />
                  Weaknesses
                </h5>
                <ul>
                  {selectedStock.weaknesses.map((weakness, index) => (
                    <li key={index}>{weakness}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="investment-actions">
              <div className="action-recommendation">
                <Triangle size={20} className="action-icon" />
                <span className="action-text">
                  {selectedStock.recommendation}
                </span>
              </div>

              <div className="investment-buttons">
                <button
                  className="watchlist-button"
                  onClick={() =>
                    setToast({
                      type: "success",
                      message: "Added to Watchlist",
                      subText: `${selectedStock.symbol} has been added to your watchlist.`,
                    })
                  }
                >
                  Add to Watchlist
                </button>

                <button
                  className="report-button"
                  onClick={() =>
                    setToast({
                      type: "info",
                      message: "Detailed Report",
                      subText:
                        "The detailed analysis report will be available in the next update.",
                    })
                  }
                >
                  Generate Full Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDetails;
